/**
 * LevNutri - Plano Alimentar com IA (GPT-4)
 * Suporte a 7 tipos de serviço da nutricionista
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { dietAIService, dietPlansService, patientsService, authService, foodDatabaseService, settingsService } from '../../services/api';
import PrintLayout, { PrintCSS } from './PrintLayout';
import useIsMobile from '../../hooks/useIsMobile';
import { useToast } from '../../contexts/ToastContext';

import { SERVICES, STRATEGIES, sum, fmt1, dayTotals, guessCategory, getSubstitutes, serviceById, uniqueServices, strategyById, MEAL_ORDER, MEAL_ICONS, empty } from './helpers';
import { S, TotalsBar, MacroBlock, MacroPill, ServiceBadge, StrategyBadge, DataChip, Spinner, MealCard } from './ui';
import { UNIDADES, emptyAlimento, FoodSearchInput, MealTotals, DailyTotalsBar, TemplatesModal, getMicrosFromAlimento, SvgDonut, AnalysisPanel, emptyRefeicao } from './widgets';
import { EscolhaModeloModal, SalvarModeloModal } from './modelos';
import { ManualTab, ClinicalSummaryCard, GeneratorTab, ValidacaoPanel, EditorTab, SupplementsSection, SupplementCard, HistoryTab, HistoryItem, ComparisonView, FoodDatabasePanel, FoodPanelItem, SupplementPanelItem, AddFoodModal, SubstituteModal } from './tabs';
import { Ico, emo } from '../ui/Ico';
import ResumoTab from './ResumoTab';
import PatientPicker from './PatientPicker';
import './resumo.css';
/* ══════════════════════════════════════════════════════════════ */
export default function PlanoAlimentar({ patientId: initialPatientId, onNavigate }) {
  const isMobile = useIsMobile();
  const { showSuccess } = useToast();
  // Modo do Banco de Alimentos: { type:'add', mealKey } ou { type:'substitute', mealKey, idx, currentFood }
  const [foodPanelMode, setFoodPanelMode] = useState({ type: 'add', mealKey: null });

  const [patients,     setPatients]     = useState([]);
  const [patientId,    setPatientId]    = useState(initialPatientId || '');
  const [patient,      setPatient]      = useState(null);
  const [view,         setView]         = useState('resumo');
  const [generating,   setGenerating]   = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [enviando,     setEnviando]     = useState(false);
  const [savedPlans,   setSavedPlans]   = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [compareIds,   setCompareIds]   = useState([]);
  const [error,        setError]        = useState('');
  const [successMsg,   setSuccessMsg]   = useState('');
  const [currentUser,  setCurrentUser]  = useState(null);

  /* ─── Sugestão IA carregada da ficha online ─── */
  const [iaSugestao, setIaSugestao] = useState(null);

  /* ─── Food Database Panel ─── */
  const [showFoodPanel, setShowFoodPanel]   = useState(false);
  const [foodPanelTab, setFoodPanelTab]     = useState('foods');
  const [allFoods, setAllFoods]             = useState([]);
  const [allSupplements, setAllSupplements] = useState([]);
  const [foodCategories, setFoodCategories] = useState([]);
  const [supCategories, setSupCategories]   = useState([]);
  const [fpCategory, setFpCategory]         = useState('');
  const [fpSupCategory, setFpSupCategory]   = useState('');
  const [fpSearch, setFpSearch]             = useState('');
  const [dbLoaded, setDbLoaded]             = useState(false);
  /* Add food modal */
  const [addFoodModal, setAddFoodModal]     = useState(null);  // { item, type }
  const [addFoodMeal, setAddFoodMeal]       = useState('');
  const [addFoodPortion, setAddFoodPortion] = useState(100);
  /* Substitute panel */
  const [subPanel, setSubPanel]             = useState(null);  // { mealKey, idx, food }
  /* Cardápios prontos (modelos) */
  const [showModelos, setShowModelos]       = useState(false);
  const [showSalvarModelo, setShowSalvarModelo] = useState(false);

  /* Campos do gerador */
  const [serviceType,    setServiceType]    = useState('consulta_feminina');
  const [strategy,       setStrategy]       = useState('');
  const [macrosWarning,  setMacrosWarning]  = useState('');
  // Resultado da validação do plano gerado contra as metas do programa
  const [validacao,      setValidacao]      = useState(null); // { checks, resumo, bloqueia_salvar, metas, alertas, conflitos }
  // Refeições definidas pela nutricionista no passo "Refeições do plano"
  const [refeicoes,        setRefeicoes]        = useState([]);
  const [refeicoesValidas, setRefeicoesValidas] = useState(false);
  const [genderFocus,    setGenderFocus]    = useState('female');
  const [clinicalSummary, setClinicalSummary] = useState(null);
  const [clinicalOpen,    setClinicalOpen]    = useState(true);

  /* Plano em edição */
  const EMPTY_PLAN = {
    id: null, plan_name:'', daily_calories:0, protein_grams:0,
    carbs_grams:0, fat_grams:0, water_recommendation:2.0,
    guidelines:'', notes:'', ai_generated:false, ai_model:'',
    service_type:'', strategy:'', meals:{}, supplements:[], refeicoes_origem: null,
  };
  const [plan, setPlan] = useState(EMPTY_PLAN);

  /* ─── carregar pacientes e usuário logado ─── */
  useEffect(() => {
    patientsService.getAll().then(r => { if (r.success) setPatients(r.data); });
    // Carrega usuário do localStorage e enriquece com CRN fresco das settings
    const base = authService.getCurrentUser() || {};
    settingsService.get().then(r => {
      if (r?.success && r.data) {
        if (r.data.crn !== undefined) setCurrentUser({ ...base, crn: r.data.crn });
        else setCurrentUser(base);
        const gf = r.data.gender_focus;
        if (gf === 'female' || gf === 'male' || gf === 'mixed') {
          setGenderFocus(gf);
          setServiceType(gf === 'male' ? 'consulta_masculina' : 'consulta_feminina');
        }
      } else {
        setCurrentUser(base);
      }
    }).catch(() => setCurrentUser(base));
  }, []);

  /* ─── Ler rascunho de sugestão IA (vindo da ficha online) ─── */
  useEffect(() => {
    if (!initialPatientId) return;
    const key = `lev_ia_rascunho_${initialPatientId}`;
    const raw = localStorage.getItem(key);
    if (!raw) return;
    try {
      const draft = JSON.parse(raw);
      if (Date.now() - draft.ts < 3600000) {
        setIaSugestao(draft);
        setPlan(prev => ({ ...prev, guidelines: draft.sugestao }));
      }
    } catch {}
    localStorage.removeItem(key);
  }, [initialPatientId]);

  useEffect(() => {
    if (!patientId) return;
    const p = patients.find(x => String(x.id) === String(patientId)) || null;
    setPatient(p);
    // Programa padrão coerente com o sexo do paciente
    const sexo = p?.gender === 'M' ? 'male' : p?.gender === 'F' ? 'female' : null;
    if (sexo) {
      setServiceType(prev => SERVICES.some(s => s.id === prev && s.gender === sexo)
        ? prev : (sexo === 'male' ? 'consulta_masculina' : 'consulta_feminina'));
    }
    if (view === 'history' || view === 'resumo') loadPlans(patientId);
  }, [patientId, patients]);

  // plansOf: de qual paciente é a lista em savedPlans (evita mostrar no Resumo
  // planos do paciente anterior enquanto a nova lista chega); plansReq descarta
  // respostas fora de ordem ao trocar de paciente rápido.
  const [plansOf, setPlansOf] = useState({ pid: null, ok: false });
  const plansReq = useRef(0);
  const loadPlans = useCallback(async (pid) => {
    const req = ++plansReq.current;
    setLoadingPlans(true);
    try {
      const r = await dietPlansService.getByPatient(pid);
      if (req !== plansReq.current) return;
      if (r.success) setSavedPlans(r.data);
      setPlansOf({ pid: String(pid), ok: !!r.success });
    } catch {
      if (req === plansReq.current) setPlansOf({ pid: String(pid), ok: false });
    } finally {
      if (req === plansReq.current) setLoadingPlans(false);
    }
  }, []);

  /* ─── Carregar banco de alimentos quando editor abre ─── */
  useEffect(() => {
    if (view !== 'editor' || dbLoaded) return;
    Promise.all([
      foodDatabaseService.getAll(),
      foodDatabaseService.getAllSupplements(),
    ]).then(([fRes, sRes]) => {
      if (fRes.success) {
        setAllFoods(fRes.data || []);
        setFoodCategories([...new Set((fRes.data || []).map(f => f.categoria))]);
      }
      if (sRes.success) {
        setAllSupplements(sRes.data || []);
        setSupCategories([...new Set((sRes.data || []).map(s => s.categoria))]);
      }
      setDbLoaded(true);
    }).catch(() => setDbLoaded(true));
  }, [view, dbLoaded]);

  /* ─── Filtered items for food panel (client-side) ─── */
  const panelFoods = useMemo(() => {
    let items = fpCategory ? allFoods.filter(f => f.categoria === fpCategory) : allFoods;
    if (fpSearch) { const q = fpSearch.toLowerCase(); items = items.filter(f => f.nome.toLowerCase().includes(q)); }
    return items.slice(0, 80);
  }, [allFoods, fpCategory, fpSearch]);

  const panelSupplements = useMemo(() => {
    let items = fpSupCategory ? allSupplements.filter(s => s.categoria === fpSupCategory) : allSupplements;
    if (fpSearch) { const q = fpSearch.toLowerCase(); items = items.filter(s => s.nome.toLowerCase().includes(q)); }
    return items.slice(0, 80);
  }, [allSupplements, fpSupCategory, fpSearch]);

  /* ─── Adicionar alimento do banco a uma refeição ─── */
  const addFoodFromDB = (mealKey, dbFood, portionGrams) => {
    const f    = portionGrams / 100;
    const item = {
      alimento:     dbFood.nome,
      porcao:       `${portionGrams}g`,
      calorias:     Math.round((dbFood.calorias_100g || 0) * f),
      proteina:     parseFloat(((dbFood.proteina_100g || 0) * f).toFixed(1)),
      carboidratos: parseFloat(((dbFood.carbo_100g    || 0) * f).toFixed(1)),
      gordura:      parseFloat(((dbFood.gordura_100g  || 0) * f).toFixed(1)),
    };
    setPlan(prev => {
      const meals = { ...prev.meals };
      const foods = [...(meals[mealKey]?.alimentos || []), item];
      meals[mealKey] = { ...meals[mealKey], alimentos: foods,
        calorias: sum(foods,'calorias'), proteina: parseFloat(fmt1(sum(foods,'proteina'))),
        carboidratos: parseFloat(fmt1(sum(foods,'carboidratos'))), gordura: parseFloat(fmt1(sum(foods,'gordura'))) };
      return { ...prev, meals };
    });
    setAddFoodModal(null);
  };

  /* ─── Substituir alimento na refeição ─── */
  const replaceFoodFromDB = (mealKey, idx, dbFood, portionGrams) => {
    const f    = portionGrams / 100;
    const item = {
      alimento:     dbFood.nome,
      porcao:       `${portionGrams}g`,
      calorias:     Math.round((dbFood.calorias_100g || 0) * f),
      proteina:     parseFloat(((dbFood.proteina_100g || 0) * f).toFixed(1)),
      carboidratos: parseFloat(((dbFood.carbo_100g    || 0) * f).toFixed(1)),
      gordura:      parseFloat(((dbFood.gordura_100g  || 0) * f).toFixed(1)),
    };
    setPlan(prev => {
      const meals = { ...prev.meals };
      const foods = [...(meals[mealKey]?.alimentos || [])];
      foods[idx] = item;
      meals[mealKey] = { ...meals[mealKey], alimentos: foods,
        calorias: sum(foods,'calorias'), proteina: parseFloat(fmt1(sum(foods,'proteina'))),
        carboidratos: parseFloat(fmt1(sum(foods,'carboidratos'))), gordura: parseFloat(fmt1(sum(foods,'gordura'))) };
      return { ...prev, meals };
    });
    setSubPanel(null);
  };

  /* ─── Abrir/fechar Banco de Alimentos com modo ─── */
  const openBankAdd = (mealKey = null) => {
    setFoodPanelMode({ type: 'add', mealKey });
    setFpSearch('');
    setShowFoodPanel(true);
  };
  const openBankSubstitute = (mealKey, idx, currentFood) => {
    setFoodPanelMode({ type: 'substitute', mealKey, idx, currentFood });
    setFpSearch('');
    setFoodPanelTab('foods');
    setShowFoodPanel(true);
  };
  const closeBank = () => {
    setShowFoodPanel(false);
    setFoodPanelMode({ type: 'add', mealKey: null });
  };

  /* ─── Adicionar suplemento do banco ─── */
  const addSupplementFromDB = (dbSup) => {
    const indicacoes = Array.isArray(dbSup.indicacoes) ? dbSup.indicacoes.slice(0, 2).join('; ') : '';
    setPlan(prev => ({
      ...prev,
      supplements: [...(prev.supplements || []), {
        nome:         dbSup.nome,
        dosagem:      dbSup.dosagem_padrao ? `${dbSup.dosagem_padrao} ${dbSup.unidade || ''}`.trim() : '',
        momento_uso:  dbSup.momento_uso  || '',
        justificativa: indicacoes,
        observacao:   dbSup.contraindicacoes || '',
      }],
    }));
  };

  /* ─── gerar dieta via IA ─── */
  const handleGenerate = async () => {
    if (!patientId)   { setError('Selecione um paciente'); return; }
    if (!serviceType) { setError('Selecione o tipo de serviço'); return; }
    if (serviceType === 'planejamento_especializado' && !strategy) {
      setError('Selecione a estratégia alimentar'); return;
    }
    setGenerating(true); setError(''); setSuccessMsg(''); setClinicalSummary(null);
    try {
      // Geração ASSÍNCRONA: dispara o job e faz polling do status. O request
      // não fica preso 30-120s (evita timeout de proxy e perda de trabalho).
      if (!refeicoesValidas) { setError('Ajuste as refeições do plano (a soma dos percentuais precisa ser 100%).'); return; }
      let start = await dietAIService.startGeneration(patientId, serviceType, strategy || null, false, refeicoes);
      // Bloqueio de segurança (menor de idade, gestante, contraindicação…): só segue com confirmação explícita
      if (start && !start.success && start.bloqueios?.length) {
        const msg = 'Atenção — a geração foi bloqueada por segurança:\n\n• ' + start.bloqueios.join('\n• ')
          + (start.alertas?.length ? '\n\nAlertas:\n• ' + start.alertas.join('\n• ') : '')
          + '\n\nDeseja gerar mesmo assim, sob sua responsabilidade clínica?';
        if (!window.confirm(msg)) { setError('Geração cancelada: ' + start.bloqueios[0]); return; }
        start = await dietAIService.startGeneration(patientId, serviceType, strategy || null, true, refeicoes);
      }
      if (!start?.success || !start?.data?.job_id) {
        setError(start?.error || start?.message || 'Não foi possível iniciar a geração.'); return;
      }
      const jobId = start.data.job_id;

      // Poll a cada 3s por até ~4 minutos
      let r = null;
      for (let i = 0; i < 80; i++) {
        await new Promise(res => setTimeout(res, 3000));
        let st;
        try { st = await dietAIService.generationStatus(jobId); } catch { continue; }
        const s = st?.data?.status;
        if (s === 'done')   { r = { success: true, data: st.data.result }; break; }
        if (s === 'failed') { r = { success: false, error: st.data.error }; break; }
      }
      if (!r) { setError('A geração está demorando mais que o esperado. Tente novamente em instantes.'); return; }

      if (r.success) {
        const d = r.data;
        if (!d?.meals) { setError('A IA não retornou um plano completo. Tente novamente.'); return; }
        setPlan({
          id:                   null,
          plan_name:            d.plan_name            || `Plano ${new Date().toLocaleDateString('pt-BR',{month:'long',year:'numeric'})}`,
          daily_calories:       d.daily_calories       || 0,
          protein_grams:        d.protein_grams        || 0,
          carbs_grams:          d.carbs_grams          || 0,
          fat_grams:            d.fat_grams            || 0,
          water_recommendation: d.water_recommendation || 2.0,
          guidelines:           d.guidelines           || '',
          notes:                '',
          ai_generated:         true,
          ai_model:             d.ai_model             || '',
          service_type:         d.service_type         || serviceType,
          strategy:             d.strategy             || strategy || '',
          meals:                d.meals                || {},
          supplements:          d.suplementacao        || [],
          refeicoes_origem:     d.refeicoes_origem     || null,
        });
        setMacrosWarning(d.validacao ? '' : (d.macros_warning || ''));
        setValidacao(d.validacao ? { ...d.validacao, metas: d.metas, alertas: d.alertas_seguranca || [], conflitos: d.conflitos || [] } : null);
        if (d.clinical_summary) {
          setClinicalSummary(d.clinical_summary);
          setClinicalOpen(true);
        }
        setView('editor');
      } else {
        setError(r.error || r.message || 'Erro ao gerar dieta.');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao conectar com a IA');
    } finally {
      setGenerating(false);
    }
  };

  /* ─── aplicar um Cardápio Pronto (modelo) ao plano em edição ─── */
  const temAlimentos = Object.values(plan.meals || {}).some(m => (m.alimentos || []).length > 0);
  const aplicarModelo = (tpl) => {
    if (temAlimentos && !window.confirm('Isto vai substituir as refeições atuais pelo modelo. Continuar?')) return;
    const rotulo = [tpl.nome, tpl.variante].filter(Boolean).join(' | ');
    setPlan(prev => ({ ...prev, plan_name: prev.plan_name || rotulo, meals: tpl.meals_plan || {} }));
    setShowModelos(false);
    setView('editor');
    setSuccessMsg(`Modelo "${rotulo}" aplicado. Revise e ajuste antes de salvar.`);
  };

  /* ─── salvar plano ─── */
  const handleSave = async () => {
    if (!patientId || !plan.plan_name) { setError('Informe o nome do plano'); return; }
    if (validacao?.bloqueia_salvar && !plan.id) {
      const itens = (validacao.checks || []).filter(c => c.status === 'erro').map(c => `${c.item}: ${c.detalhe}`);
      if (!window.confirm('O plano gerado tem pendências importantes:\n\n• ' + itens.join('\n• ')
        + '\n\nConfirme que revisou e ajustou esses itens antes de salvar.')) return;
    }
    setSaving(true); setError('');
    try {
      const payload = {
        ...plan,
        patient_id:   patientId,
        // Resumo salvo = soma real dos alimentos (fonte única da verdade)
        daily_calories: Math.round(totals.cal) || 0,
        protein_grams:  Math.round(totals.prot * 10) / 10 || 0,
        carbs_grams:    Math.round(totals.carb * 10) / 10 || 0,
        fat_grams:      Math.round(totals.fat  * 10) / 10 || 0,
        meals_plan:   plan.meals,
        suplementacao: plan.supplements,
      };
      const r = plan.id
        ? await dietPlansService.update(plan.id, payload)
        : await dietPlansService.create(payload);
      if (r.success) {
        setSuccessMsg(r.message || 'Plano salvo!');
        setPlan(prev => ({ ...prev, id: r.data.id }));
        loadPlans(patientId);
      } else {
        setError(r.error || 'Erro ao salvar');
      }
    } catch (e) {
      setError('Erro ao salvar plano');
    } finally {
      setSaving(false);
    }
  };

  /* ─── enviar plano ao paciente (WhatsApp + e-mail) — Fase 5.4 ─── */
  const handleEnviarPaciente = async () => {
    if (!plan.id) { setError('Salve o plano antes de enviar ao paciente.'); return; }
    setEnviando(true); setError(''); setSuccessMsg('');
    try {
      const r = await dietPlansService.enviarAoPaciente(plan.id);
      if (r.success) {
        const c = r.data?.canais || {};
        const partes = [];
        if (c.whatsapp) partes.push(`WhatsApp: ${c.whatsapp.ok ? '✓ ' + c.whatsapp.motivo : '✕ ' + c.whatsapp.motivo}`);
        if (c.email)    partes.push(`E-mail: ${c.email.ok ? '✓ enviado' : '✕ ' + c.email.motivo}`);
        setSuccessMsg((r.data?.algum_sucesso ? 'Plano enviado! ' : '') + partes.join(' · '));
      } else {
        setError(r.error || r.message || 'Não foi possível enviar o plano');
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao enviar o plano ao paciente');
    } finally {
      setEnviando(false);
    }
  };

  /* ─── abrir plano salvo ─── */
  const handleOpenPlan = async (planId) => {
    try {
      const r = await dietPlansService.getById(planId);
      if (r.success) {
        const d = r.data;
        setPlan({
          id:                   d.id,
          plan_name:            d.plan_name            || '',
          daily_calories:       d.daily_calories       || 0,
          protein_grams:        d.protein_grams        || 0,
          carbs_grams:          d.carbs_grams          || 0,
          fat_grams:            d.fat_grams            || 0,
          water_recommendation: d.water_recommendation || 2.0,
          guidelines:           d.guidelines           || '',
          notes:                d.notes                || '',
          ai_generated:         !!d.ai_generated,
          ai_model:             d.ai_model             || '',
          service_type:         d.service_type         || '',
          strategy:             d.strategy             || '',
          meals:                d.meals_plan           || {},
          supplements:          d.supplements          || [],
        });
        setValidacao(null); setMacrosWarning('');
        setView('editor'); setSuccessMsg(''); setError('');
      }
    } catch (e) { setError('Erro ao abrir plano'); }
  };

  /* ─── deletar plano ─── */
  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Remover este plano alimentar?')) return;
    try {
      const r = await dietPlansService.delete(planId);
      if (r.success) {
        setCompareIds(prev => prev.filter(id => id !== planId));
        loadPlans(patientId);
      }
    } catch (e) { setError('Erro ao remover plano'); }
  };

  /* ─── reativar plano ─── */
  const handleReactivatePlan = async (planId) => {
    try {
      const r = await dietPlansService.update(planId, { status: 'ativo' });
      if (r.success) loadPlans(patientId);
    } catch (e) { setError('Erro ao reativar plano'); }
  };

  /* ─── alternar seleção para comparação ─── */
  const handleToggleCompare = (planId) => {
    setCompareIds(prev => {
      if (prev.includes(planId)) return prev.filter(id => id !== planId);
      if (prev.length >= 2) return [prev[1], planId];
      return [...prev, planId];
    });
  };

  /* ─── edição inline ─── */
  const updateFood = (mealKey, idx, field, value) => {
    setPlan(prev => {
      const meals = { ...prev.meals };
      const foods = [...(meals[mealKey]?.alimentos || [])];
      foods[idx] = {
        ...foods[idx],
        [field]: (field === 'alimento' || field === 'porcao') ? value : (parseFloat(value) || 0),
      };
      const cal  = sum(foods, 'calorias');
      const prot = parseFloat(fmt1(sum(foods, 'proteina')));
      const carb = parseFloat(fmt1(sum(foods, 'carboidratos')));
      const fat  = parseFloat(fmt1(sum(foods, 'gordura')));
      meals[mealKey] = { ...meals[mealKey], alimentos: foods, calorias: cal, proteina: prot, carboidratos: carb, gordura: fat };
      return { ...prev, meals };
    });
  };

  const addFood = (mealKey) => {
    setPlan(prev => {
      const meals = { ...prev.meals };
      meals[mealKey] = { ...meals[mealKey], alimentos: [...(meals[mealKey]?.alimentos || []), empty()] };
      return { ...prev, meals };
    });
  };

  const removeFood = (mealKey, idx) => {
    setPlan(prev => {
      const meals = { ...prev.meals };
      const foods = (meals[mealKey]?.alimentos || []).filter((_, i) => i !== idx);
      meals[mealKey] = { ...meals[mealKey], alimentos: foods, calorias: sum(foods, 'calorias') };
      return { ...prev, meals };
    });
  };

  /* ─── suplementos ─── */
  const EMPTY_SUP = { nome:'', dosagem:'', momento_uso:'', justificativa:'', observacao:'' };

  const updateSupplement = (idx, field, value) => {
    setPlan(prev => {
      const supplements = [...(prev.supplements || [])];
      supplements[idx] = { ...supplements[idx], [field]: value };
      return { ...prev, supplements };
    });
  };
  const addSupplement = () => {
    setPlan(prev => ({ ...prev, supplements: [...(prev.supplements || []), { ...EMPTY_SUP }] }));
  };
  const removeSupplement = (idx) => {
    setPlan(prev => ({ ...prev, supplements: (prev.supplements || []).filter((_, i) => i !== idx) }));
  };

  const totals = dayTotals(plan.meals);
  const svc    = serviceById(plan.service_type || serviceType);

  /* ══════ RENDER ══════ */
  return (
    <div className="pa-page" style={{ ...S.page, maxWidth: showFoodPanel ? '1440px' : '1100px' }}>
      <PrintCSS />

      {/* PrintLayout — invisível na tela, visível apenas na impressão */}
      {view === 'editor' && (
        <PrintLayout plan={plan} patient={patient} user={currentUser} supplements={plan.supplements} />
      )}

      {/* Barra superior fixa: título + busca de paciente + ação principal */}
      <div className="pa-topbar no-print">
        <div className="pa-topbar-in">
          {onNavigate && (
            <button type="button" className="pa-back" title="Voltar" aria-label="Voltar"
              onClick={() => onNavigate(initialPatientId ? 'patient-detail' : 'patients', initialPatientId)}>
              <Ico e="◀" size={16} />
            </button>
          )}
          <h1 className="pa-title"><Ico e="🥗" size={20} />Plano Alimentar</h1>
          <PatientPicker
            patients={patients}
            value={patientId}
            onChange={id => { setPatientId(id); setError(''); setSuccessMsg(''); }}
          />
          {patientId && view !== 'editor' && view !== 'generator' && (
            <div className="pa-top-actions">
              <button type="button" className="pa-btn pa-btn-primary" onClick={() => setView('generator')}>
                <Ico e="✨" />Novo plano
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Ações do editor (inalteradas) */}
      {view === 'editor' && patientId && (
        <div style={{ ...S.header, justifyContent:'flex-end' }} className="no-print">
          <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
            <button
              style={{ ...S.outlineBtn, ...(showFoodPanel ? { background:'#fbf8f5', color:'#D4A574', border:'1.5px solid #D4A574', fontWeight:600 } : {}) }}
              onClick={() => { if (showFoodPanel) closeBank(); else openBankAdd(null); }}>
              <Ico e="🥩" /> {showFoodPanel ? 'Fechar Banco' : 'Banco de Alimentos'}
            </button>
            <button style={S.outlineBtn} onClick={() => setShowModelos(true)}><Ico e="📋" sp />Modelos</button>
            {temAlimentos && (
              <button style={S.outlineBtn} onClick={() => setShowSalvarModelo(true)}><Ico e="⭐" sp />Salvar como modelo</button>
            )}
            <button style={S.outlineBtn} onClick={() => window.print()}><Ico e="🖨" sp />Imprimir</button>
            {plan.id && (
              <button
                style={{ ...S.outlineBtn, background:'#edf4ee', color:'#2f5236', border:'1.5px solid #b9d2bd', fontWeight:600 }}
                onClick={handleEnviarPaciente}
                disabled={enviando}
                title="Enviar o PDF do plano ao paciente por WhatsApp e e-mail">
                {emo(enviando ? 'Enviando...' : '📤 Enviar ao paciente')}
              </button>
            )}
            <button style={S.saveBtn} onClick={handleSave} disabled={saving}>
              {emo(saving ? 'Salvando...' : (plan.id ? '💾 Atualizar' : '💾 Salvar Plano'))}
            </button>
          </div>
        </div>
      )}

      {error      && <div style={S.errorBox}  className="no-print">{error}</div>}
      {successMsg && <div style={S.successBox} className="no-print">{emo(successMsg)}</div>}

      {/* Nenhum paciente: convite à busca em vez de tela vazia */}
      {!patientId && (
        <div className="pa-card pa-empty pa-fade no-print">
          <div className="pa-empty-ico"><Ico e="🔍" size={24} /></div>
          <h3>Qual paciente você vai atender?</h3>
          <p>Busque pelo nome na barra acima para ver, de cara, o último plano alimentar passado e os anteriores.</p>
          <div className="pa-empty-row">
            <span className="pa-kbd">/</span><span className="pa-hint">foca a busca de qualquer lugar da tela</span>
          </div>
        </div>
      )}

      {patientId && (
        <>
          {/* Banner: Sugestão IA carregada da ficha online */}
          {iaSugestao && (
            <div style={{ background: '#edf1f6', border: '1.5px solid #b8c7d8', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }} className="no-print">
              <span style={{ fontSize: '1.4rem', flexShrink: 0 }}><Ico e="🤖" /></span>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600, color: '#7189a6', fontSize: '0.92rem' }}>Sugestão gerada por IA — revise e adapte antes de finalizar</span>
                <span style={{ fontSize: '0.82rem', color: '#736b64', marginLeft: '0.5rem' }}>
                  As orientações do plano foram pré-preenchidas com base na análise dos exames{iaSugestao.nome_paciente ? ` de ${iaSugestao.nome_paciente}` : ''}.
                </span>
              </div>
              <button onClick={() => setView('editor')}
                style={{ background: '#ffffff', color: '#2f2a26', border: '1px solid #d9d0c6', borderRadius: '7px', padding: '0.4rem 0.85rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                Ver orientações →
              </button>
              <button onClick={() => setIaSugestao(null)}
                style={{ background: 'none', border: 'none', color: '#736b64', cursor: 'pointer', fontSize: '1rem', padding: '2px 6px', flexShrink: 0 }}><Ico e="✕" /></button>
            </div>
          )}

          {/* Validação do plano gerado × metas do programa */}
          {validacao && <ValidacaoPanel validacao={validacao} onClose={() => setValidacao(null)} />}

          {/* Banner: aviso de desvio calórico */}
          {macrosWarning && (
            <div style={{ background: '#fbf8f5', border: '1.5px solid #c4933f', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }} className="no-print">
              <span style={{ fontSize: '1.3rem', flexShrink: 0 }}><Ico e="⚠" /></span>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600, color: '#8a5f1a', fontSize: '0.88rem', display: 'block' }}>Desvio calórico detectado</span>
                <span style={{ fontSize: '0.82rem', color: '#5a3e2b' }}>{macrosWarning}</span>
              </div>
              <button onClick={() => setMacrosWarning('')}
                style={{ background: 'none', border: 'none', color: '#736b64', cursor: 'pointer', fontSize: '1rem', padding: '2px 6px', flexShrink: 0 }}><Ico e="✕" /></button>
            </div>
          )}

          {/* Resumo Clínico IA */}
          {clinicalSummary && (
            <ClinicalSummaryCard
              summary={clinicalSummary}
              open={clinicalOpen}
              onToggle={() => setClinicalOpen(o => !o)}
            />
          )}

          {/* Tabs */}
          <div style={S.tabs} className="no-print">
            {emo([
              { key:'resumo',    label:'📊 Resumo' },
              { key:'generator', label:'✨ Plano Inteligente IA' },
              { key:'editor',    label:'✏️ Editor de Plano' },
              { key:'manual',    label:'📝 Plano Manual' },
              { key:'history',   label:'📁 Histórico' },
            ].map(t => (
              <button key={t.key}
                style={{ ...S.tab, ...(view === t.key ? S.tabActive : {}) }}
                onClick={() => { setView(t.key); if (t.key === 'history' || t.key === 'resumo') loadPlans(patientId); }}>
                {emo(t.label)}
              </button>
            )))}
          </div>

          {/* ══ TAB: RESUMO (consulta rápida, só leitura) ══ */}
          {view === 'resumo' && (
            <ResumoTab
              key={patientId}
              patient={patient}
              plans={plansOf.pid === String(patientId) && plansOf.ok ? savedPlans : []}
              status={plansOf.pid !== String(patientId) ? 'loading'
                : plansOf.ok ? 'ok' : (loadingPlans ? 'loading' : 'error')}
              onRetry={() => loadPlans(patientId)}
              onOpenEditor={handleOpenPlan}
              onNew={() => setView('generator')}
              onManual={() => setView('manual')}
              onModelos={() => setShowModelos(true)}
              onShowHistory={() => { setView('history'); loadPlans(patientId); }}
            />
          )}

          {/* ══ TAB: GERADOR ══ */}
          {view === 'generator' && (
            <GeneratorTab
              patient={patient}
              serviceType={serviceType}  setServiceType={setServiceType}
              strategy={strategy}        setStrategy={setStrategy}
              generating={generating}
              onGenerate={handleGenerate}
              genderFocus={genderFocus}
              refeicoes={refeicoes}               setRefeicoes={setRefeicoes}
              refeicoesValidas={refeicoesValidas} setRefeicoesValidas={setRefeicoesValidas}
            />
          )}

          {/* ══ TAB: EDITOR ══ */}
          {view === 'editor' && (
            <div style={{ display:'flex', gap:'1.25rem', alignItems:'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flex:1, minWidth:0, width: isMobile ? '100%' : undefined }}>
                <EditorTab
                  plan={plan} setPlan={setPlan} totals={totals}
                  updateFood={updateFood} addFood={addFood} removeFood={removeFood}
                  updateSupplement={updateSupplement}
                  addSupplement={addSupplement}
                  removeSupplement={removeSupplement}
                  onSubstitute={(mealKey, idx, food) => openBankSubstitute(mealKey, idx, food)}
                  onBrowseBank={(mealKey) => openBankAdd(mealKey)}
                />
              </div>
              {showFoodPanel && (
                <FoodDatabasePanel
                  isMobile={isMobile}           onClose={closeBank}
                  mode={foodPanelMode.type}
                  currentName={foodPanelMode.currentFood?.alimento}
                  targetMealName={foodPanelMode.mealKey ? (plan.meals?.[foodPanelMode.mealKey]?.nome || foodPanelMode.mealKey) : null}
                  tab={foodPanelTab}             onTabChange={setFoodPanelTab}
                  foods={panelFoods}             supplements={panelSupplements}
                  categories={foodCategories}    supCategories={supCategories}
                  category={fpCategory}          onCategoryChange={c => { setFpCategory(c); setFpSearch(''); }}
                  supCategory={fpSupCategory}    onSupCategoryChange={c => { setFpSupCategory(c); setFpSearch(''); }}
                  search={fpSearch}              onSearchChange={setFpSearch}
                  dbLoaded={dbLoaded}
                  mealKeys={Object.keys(plan.meals || {})}
                  mealNames={Object.fromEntries(Object.entries(plan.meals || {}).map(([k,m]) => [k, m.nome || k]))}
                  onAddFood={(food) => {
                    if (foodPanelMode.type === 'substitute') {
                      setAddFoodModal({ item: food, type: 'food', mode: 'substitute',
                        mealKey: foodPanelMode.mealKey, idx: foodPanelMode.idx,
                        currentName: foodPanelMode.currentFood?.alimento || '' });
                      setAddFoodMeal(foodPanelMode.mealKey);
                    } else {
                      const meal = foodPanelMode.mealKey || Object.keys(plan.meals || {})[0] || '';
                      setAddFoodModal({ item: food, type: 'food', mode: 'add' });
                      setAddFoodMeal(meal);
                    }
                    setAddFoodPortion(food.porcao_padrao || 100);
                  }}
                  onAddSupplement={addSupplementFromDB}
                />
              )}
            </div>
          )}

          {/* ══ MODAIS ══ */}
          {addFoodModal?.type === 'food' && (
            <AddFoodModal
              food={addFoodModal.item}
              mode={addFoodModal.mode}
              currentName={addFoodModal.currentName}
              mealKeys={Object.keys(plan.meals || {})}
              mealNames={Object.fromEntries(Object.entries(plan.meals || {}).map(([k,m]) => [k, m.nome || k]))}
              selectedMeal={addFoodMeal}  onMealChange={setAddFoodMeal}
              portion={addFoodPortion}    onPortionChange={setAddFoodPortion}
              onConfirm={() => {
                if (addFoodModal.mode === 'substitute') {
                  replaceFoodFromDB(addFoodModal.mealKey, addFoodModal.idx, addFoodModal.item, addFoodPortion);
                  setAddFoodModal(null);
                  closeBank();
                  showSuccess(`${addFoodModal.currentName} substituído por ${addFoodModal.item.nome} ✓`);
                } else {
                  addFoodFromDB(addFoodMeal, addFoodModal.item, addFoodPortion);
                  showSuccess(`${addFoodModal.item.nome} adicionado ✓`);
                }
              }}
              onClose={() => setAddFoodModal(null)}
            />
          )}
          {subPanel && (
            <SubstituteModal
              food={subPanel.food}
              substitutes={getSubstitutes(subPanel.food.alimento, subPanel.food.calorias, subPanel.food.porcao, allFoods)}
              onSelect={(dbFood, portion) => replaceFoodFromDB(subPanel.mealKey, subPanel.idx, dbFood, portion)}
              onBrowse={() => { setSubPanel(null); setShowFoodPanel(true); setFpSearch(subPanel.food.alimento); }}
              onClose={() => setSubPanel(null)}
            />
          )}

          {/* ══ Cardápios Prontos (modelos) ══ */}
          {showModelos && (
            <EscolhaModeloModal
              onApply={aplicarModelo}
              onBlank={() => { setShowModelos(false); setView('editor'); }}
              onClose={() => setShowModelos(false)}
            />
          )}
          {showSalvarModelo && (
            <SalvarModeloModal
              meals={plan.meals}
              defaultName={plan.plan_name}
              onSaved={(msg) => { setShowSalvarModelo(false); setSuccessMsg(msg); }}
              onClose={() => setShowSalvarModelo(false)}
            />
          )}

          {/* ══ TAB: PLANO MANUAL ══ */}
          {view === 'manual' && (
            <ManualTab
              patientId={patientId}
              onSaved={() => { loadPlans(patientId); setView('history'); }}
            />
          )}

          {/* ══ TAB: HISTÓRICO ══ */}
          {view === 'history' && (
            <HistoryTab
              patient={patient}
              plans={savedPlans}
              loading={loadingPlans}
              onOpen={handleOpenPlan}
              onDelete={handleDeletePlan}
              onNew={() => setView('generator')}
              compareIds={compareIds}
              onToggleCompare={handleToggleCompare}
              onReactivate={handleReactivatePlan}
            />
          )}
        </>
      )}
    </div>
  );
}

